import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Box, CircularProgress } from '@mui/material';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BranchProvider, useBranch } from './contexts/BranchContext';
import { OrderProvider } from './contexts/OrderContext';
import { SocketProvider } from './contexts/SocketContext';

import LoginPage from './pages/LoginPage';
import NoOutletPage from './pages/NoOutletPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import TotalOrdersPage from './pages/TotalOrdersPage';
import MenuPage from './pages/MenuPage';
import SettingsPage from './pages/SettingsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import DevSmokePage from './pages/DevSmokePage';
import FoodDetailPage from './pages/FoodDetailPage';
import BottomNav from './components/BottomNav';
import BranchSelectModal from './components/BranchSelectModal';
import GlobalOrderModal from './components/GlobalOrderModal';

import './theme';

const theme = createTheme({
  palette: {
    primary: {
      main: '#851E41',
    },
    secondary: {
      main: '#2C3E50',
    },
    background: {
      default: '#F8FAFC',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
    },
  },
});

const LoadingFallback = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#F8FAFC',
    }}
  >
    <CircularProgress sx={{ color: '#851E41' }} />
  </Box>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
};

const HasOutletAccess = ({ children }) => {
  const { hasOutletAccess, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!hasOutletAccess) {
    return <Navigate to="/no-outlet" replace />;
  }

  return children;
};

const MainLayout = ({ children }) => {
  const { selectedBranch, isLoading: branchLoading } = useBranch();
  
  if (branchLoading) {
    return <LoadingFallback />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', pb: 7 }}>
      {children}
      <BranchSelectModal />
      <BottomNav />
    </Box>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/no-outlet" element={<NoOutletPage />} />
      <Route path="/__smoke" element={<DevSmokePage />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <OrdersPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <OrdersPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-details"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <OrderDetailsPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/total-orders"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <TotalOrdersPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />

      <Route
        path="/menu"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <MenuPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <SubscriptionPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <MainLayout>
                <SettingsPage />
              </MainLayout>
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/food-detail"
        element={
          <ProtectedRoute>
            <HasOutletAccess>
              <FoodDetailPage />
            </HasOutletAccess>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AppContent = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BranchProvider>
          <OrderProvider>
            <SocketProvider>
              <AppRoutes />
              <GlobalOrderModal />
            </SocketProvider>
          </OrderProvider>
        </BranchProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent />
    </ThemeProvider>
  );
}